import { useEffect, useRef, useState } from "react";
import type { ModeConfig } from "../state/modeConfig";
import { useGameSession } from "../session/useGameSession";
import { generateDigitSequence } from "../logic/sequences";
import type { usePlayerState } from "../hooks/usePlayerState";
import { TopBar } from "../components/TopBar";
import { NumberPad } from "../components/NumberPad";
import { Instructions } from "../screens/Instructions";
import { Results } from "../screens/Results";
import { FeedbackOverlay } from "../components/FeedbackOverlay";
import { CountdownOverlay } from "../components/CountdownOverlay";
import { MODE_COUNTDOWN_MS } from "../state/modeConfig";
import { useTranslation } from "../i18n/useTranslation";

interface DigitSpanProps {
  config: ModeConfig;
  player: ReturnType<typeof usePlayerState>;
  onExit: () => void;
}

type SubPhase = "presenting" | "recalling";

const SHOW_TIME_MS = 700;
const GAP_TIME_MS = 200;

export function DigitSpan({ config, player, onExit }: DigitSpanProps) {
  const game = useGameSession(config.modeId, player);
  const { session } = game;
  const { t } = useTranslation();

  const [sequence, setSequence] = useState<number[]>([]);
  const [playerInput, setPlayerInput] = useState<number[]>([]);
  const [subPhase, setSubPhase] = useState<SubPhase>("presenting");
  const [presentIndex, setPresentIndex] = useState(0);
  const reverseMode = session.difficulty >= 8;

  const lastRoundRef = useRef<number>(-1);
  useEffect(() => {
    if (session.phase.kind !== "playing") return;
    if (lastRoundRef.current === session.roundNum) return;
    lastRoundRef.current = session.roundNum;
    startRound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.phase, session.roundNum]);

  function startRound(): void {
    const seq = generateDigitSequence(session.difficulty);
    setSequence(seq);
    setPlayerInput([]);
    setSubPhase("presenting");
    setPresentIndex(0);
  }

  useEffect(() => {
    if (subPhase !== "presenting" || sequence.length === 0) return;
    if (presentIndex >= sequence.length) {
      setSubPhase("recalling");
      return;
    }
    const id = window.setTimeout(() => {
      setPresentIndex((i) => i + 1);
    }, SHOW_TIME_MS + GAP_TIME_MS);
    return () => window.clearTimeout(id);
  }, [subPhase, sequence, presentIndex]);

  function onDigit(d: number): void {
    if (subPhase !== "recalling") return;
    if (playerInput.length < sequence.length) {
      setPlayerInput([...playerInput, d]);
    }
  }
  function onBackspace(): void {
    if (subPhase !== "recalling" || playerInput.length === 0) return;
    setPlayerInput(playerInput.slice(0, -1));
  }
  function onSubmit(): void {
    if (subPhase !== "recalling") return;
    const expected = reverseMode ? [...sequence].reverse() : sequence;
    const correct =
      playerInput.length === expected.length &&
      playerInput.every((v, i) => v === expected[i]);
    game.submitAnswer(correct);
  }

  if (session.phase.kind === "instructions") {
    return (
      <Instructions
        config={config}
        difficulty={session.difficulty}
        highScore={session.playerState.modeHighScores[config.modeId] ?? 0}
        instructions={[
          t("digitSpan.instr.show", { n: session.difficulty }),
          t("digitSpan.instr.watch"),
          reverseMode
            ? t("digitSpan.instr.reverse")
            : t("digitSpan.instr.forward"),
          t("digitSpan.instr.keyboard"),
        ]}
        difficultyDescription={t("digitSpan.diffDesc", {
          n: session.difficulty,
          reverse: reverseMode,
        })}
        onStart={game.startPlaying}
        onBack={onExit}
        onDifficultyChange={game.setDifficulty}
      />
    );
  }

  if (session.phase.kind === "results") {
    return (
      <Results
        modeName={t(`mode.${config.modeId}.name`)}
        result={session.sessionResult}
        level={session.playerState.level}
        xpProgressFrac={session.playerState.xpProgressFrac}
        onContinue={onExit}
      />
    );
  }

  return (
    <main className="min-h-screen flex flex-col relative">
      <TopBar
        modeName={t(`mode.${config.modeId}.name`)}
        score={session.score}
        streak={session.streak.current}
        roundNumber={Math.min(session.roundNum + 1, session.maxRounds)}
        totalRounds={session.maxRounds}
      />

      <section className="flex-1 flex flex-col items-center justify-center px-6 gap-8 relative">
        {subPhase === "presenting" ? (
          <PresentingView sequence={sequence} presentIndex={presentIndex} />
        ) : (
          <RecallingView
            sequence={sequence}
            playerInput={playerInput}
            reverse={reverseMode}
            onDigit={onDigit}
            onBackspace={onBackspace}
            onSubmit={onSubmit}
          />
        )}

        {session.phase.kind === "countdown" && (
          <CountdownOverlay
            durationMs={MODE_COUNTDOWN_MS[config.modeId] ?? 0}
            onSkip={game.finishCountdown}
          />
        )}

        {session.phase.kind === "feedback" && (
          <FeedbackOverlay
            correct={session.phase.correct}
            detail={
              session.phase.correct
                ? undefined
                : t("common.answer", {
                    answer: (reverseMode
                      ? [...sequence].reverse()
                      : sequence
                    ).join(" "),
                  })
            }
          />
        )}
      </section>
    </main>
  );
}

function PresentingView({
  sequence,
  presentIndex,
}: {
  sequence: number[];
  presentIndex: number;
}) {
  const { t } = useTranslation();
  const current = sequence[presentIndex];
  return (
    <>
      <span className="font-mono text-xs tracking-[0.18em] uppercase text-cyan">
        {t("digitSpan.memorize")}
      </span>
      <div className="w-40 h-40 rounded-full border-4 border-cyan flex items-center justify-center">
        {current !== undefined && (
          <span className="text-7xl font-bold font-mono tabular-nums text-fg-0">
            {current}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        {sequence.map((_, i) => (
          <span
            key={i}
            className={[
              "w-2.5 h-2.5 rounded-full",
              i <= presentIndex ? "bg-cyan" : "bg-bg-3",
            ].join(" ")}
          />
        ))}
      </div>
    </>
  );
}

function RecallingView({
  sequence,
  playerInput,
  reverse,
  onDigit,
  onBackspace,
  onSubmit,
}: {
  sequence: number[];
  playerInput: number[];
  reverse: boolean;
  onDigit: (d: number) => void;
  onBackspace: () => void;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="w-full flex flex-col items-center gap-6">
      <span className="font-mono text-xs tracking-[0.18em] uppercase text-fg-2">
        {reverse ? t("digitSpan.typeReverse") : t("digitSpan.typeForward")}
      </span>
      <div className="flex gap-2 justify-center flex-wrap max-w-md">
        {sequence.map((_, i) => {
          const filled = i < playerInput.length;
          const active = i === playerInput.length;
          return (
            <div
              key={i}
              className={[
                "w-12 h-12 rounded-lg bg-bg-2 border-2 flex items-center justify-center text-2xl font-bold font-mono tabular-nums",
                filled
                  ? "border-cyan text-fg-0"
                  : active
                    ? "border-cyan/60 text-fg-3"
                    : "border-[var(--color-line)] text-fg-3",
              ].join(" ")}
            >
              {filled ? playerInput[i] : ""}
            </div>
          );
        })}
      </div>
      <NumberPad
        onDigit={onDigit}
        onBackspace={onBackspace}
        onSubmit={onSubmit}
        submitDisabled={playerInput.length === 0}
      />
    </div>
  );
}
